/**
 * Loads and validates the question bank.
 *
 * Two places are scanned automatically — you never have to edit code to add
 * questions:
 *
 *   1. src/data/questions.json          (the simple drop-in file)
 *   2. src/data/questions/<any>.json    (one file per topic — recommended)
 *
 * Both use the same shape:  { "questions": [ …Question objects… ] }
 *
 * Anything that fails validation is skipped and reported (console warning in
 * development, and on the "Progress Details" screen) instead of crashing the app.
 */
import type { Question, RoadSign } from '../types';
import { isCategoryId } from './categories';
import mainBank from './questions.json';
import signsFile from './signs.json';

export interface BankIssue {
  origin: string;
  id: string | null;
  message: string;
}

export interface BankLoadResult {
  questions: Question[];
  issues: BankIssue[];
}

const DIFFICULTIES = new Set(['easy', 'medium', 'hard']);

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Validates one raw object from JSON. Returns the typed Question, or an error
 * message explaining exactly what is wrong so it is easy to fix by hand.
 */
export function validateQuestion(
  raw: unknown,
  origin: string,
): { ok: true; value: Question } | { ok: false; issue: BankIssue } {
  const fail = (message: string, id: string | null = null): { ok: false; issue: BankIssue } => ({
    ok: false,
    issue: { origin, id, message },
  });

  if (typeof raw !== 'object' || raw === null) return fail('Entry is not an object.');
  const q = raw as Record<string, unknown>;
  const id = typeof q.id === 'string' ? q.id : null;

  if (!isNonEmptyString(q.id)) return fail('Missing "id".', id);
  if (!isNonEmptyString(q.category)) return fail('Missing "category".', id);
  if (!isCategoryId(q.category)) return fail(`Unknown category "${q.category}".`, id);
  if (!isNonEmptyString(q.questionEn)) return fail('Missing "questionEn".', id);
  if (!isNonEmptyString(q.questionAr)) return fail('Missing "questionAr".', id);

  if (!Array.isArray(q.answersEn) || q.answersEn.length < 2)
    return fail('"answersEn" needs at least 2 choices.', id);
  if (!Array.isArray(q.answersAr) || q.answersAr.length !== q.answersEn.length)
    return fail('"answersAr" must have exactly as many items as "answersEn".', id);
  if (!q.answersEn.every(isNonEmptyString)) return fail('"answersEn" has an empty choice.', id);
  if (!q.answersAr.every(isNonEmptyString)) return fail('"answersAr" has an empty choice.', id);

  if (
    typeof q.correctIndex !== 'number' ||
    !Number.isInteger(q.correctIndex) ||
    q.correctIndex < 0 ||
    q.correctIndex >= q.answersEn.length
  )
    return fail(`"correctIndex" must be a whole number between 0 and ${q.answersEn.length - 1}.`, id);

  let image: Question['image'];
  if (q.image !== undefined && q.image !== null) {
    const img = q.image as Record<string, unknown>;
    if (!isNonEmptyString(img.src)) return fail('"image.src" is missing.', id);
    image = {
      src: img.src,
      altEn: isNonEmptyString(img.altEn) ? img.altEn : '',
      altAr: isNonEmptyString(img.altAr) ? img.altAr : '',
    };
  }

  const difficulty = isNonEmptyString(q.difficulty) && DIFFICULTIES.has(q.difficulty) ? q.difficulty : 'medium';

  return {
    ok: true,
    value: {
      id: q.id,
      category: q.category,
      questionEn: q.questionEn,
      questionAr: q.questionAr,
      answersEn: q.answersEn as string[],
      answersAr: q.answersAr as string[],
      correctIndex: q.correctIndex,
      image,
      explanationEn: isNonEmptyString(q.explanationEn) ? q.explanationEn : undefined,
      explanationAr: isNonEmptyString(q.explanationAr) ? q.explanationAr : undefined,
      difficulty: difficulty as Question['difficulty'],
      source: isNonEmptyString(q.source) ? q.source : 'Unknown source',
      tags: Array.isArray(q.tags) ? (q.tags.filter(isNonEmptyString) as string[]) : [],
      isCustom: q.isCustom === true,
    },
  };
}

function collect(rawList: unknown, origin: string, out: Question[], issues: BankIssue[], seen: Set<string>) {
  if (!Array.isArray(rawList)) {
    issues.push({ origin, id: null, message: 'File has no "questions" array.' });
    return;
  }
  for (const raw of rawList) {
    const result = validateQuestion(raw, origin);
    if (!result.ok) {
      issues.push(result.issue);
      continue;
    }
    if (seen.has(result.value.id)) {
      issues.push({ origin, id: result.value.id, message: 'Duplicate id — this copy was skipped.' });
      continue;
    }
    seen.add(result.value.id);
    out.push(result.value);
  }
}

function loadStaticBank(): BankLoadResult {
  const questions: Question[] = [];
  const issues: BankIssue[] = [];
  const seen = new Set<string>();

  // Every JSON file in src/data/questions/ is picked up automatically.
  const topicFiles = import.meta.glob('./questions/*.json', { eager: true }) as Record<
    string,
    { questions?: unknown; default?: { questions?: unknown } }
  >;
  const paths = Object.keys(topicFiles).sort();
  for (const path of paths) {
    const mod = topicFiles[path];
    const payload = mod.questions ?? mod.default?.questions;
    collect(payload, path.replace('./', 'src/data/'), questions, issues, seen);
  }

  collect(mainBank.questions, 'src/data/questions.json', questions, issues, seen);

  return { questions, issues };
}

export const STATIC_BANK: BankLoadResult = loadStaticBank();

/* ------------------------------------------------------------------ */
/* Road sign deck                                                      */
/* ------------------------------------------------------------------ */

function loadSigns(): { signs: RoadSign[]; issues: BankIssue[] } {
  const signs: RoadSign[] = [];
  const issues: BankIssue[] = [];
  const raw = (signsFile as { signs?: unknown }).signs;
  if (!Array.isArray(raw)) {
    issues.push({ origin: 'src/data/signs.json', id: null, message: 'File has no "signs" array.' });
    return { signs, issues };
  }
  const seen = new Set<string>();
  for (const item of raw) {
    const s = item as Record<string, unknown>;
    if (
      !isNonEmptyString(s.id) ||
      !isNonEmptyString(s.image) ||
      !isNonEmptyString(s.nameEn) ||
      !isNonEmptyString(s.nameAr) ||
      !isNonEmptyString(s.meaningEn) ||
      !isNonEmptyString(s.meaningAr)
    ) {
      issues.push({
        origin: 'src/data/signs.json',
        id: typeof s.id === 'string' ? s.id : null,
        message: 'A sign is missing one of: id, image, nameEn, nameAr, meaningEn, meaningAr.',
      });
      continue;
    }
    if (seen.has(s.id)) {
      issues.push({ origin: 'src/data/signs.json', id: s.id, message: 'Duplicate sign id — skipped.' });
      continue;
    }
    seen.add(s.id);
    signs.push({
      id: s.id,
      image: s.image,
      nameEn: s.nameEn,
      nameAr: s.nameAr,
      meaningEn: s.meaningEn,
      meaningAr: s.meaningAr,
      type: (isNonEmptyString(s.type) ? s.type : 'warning') as RoadSign['type'],
      source: isNonEmptyString(s.source) ? s.source : 'Unknown source',
    });
  }
  return { signs, issues };
}

const signBank = loadSigns();
export const ROAD_SIGNS: RoadSign[] = signBank.signs;
export const SIGN_ISSUES: BankIssue[] = signBank.issues;

if (import.meta.env.DEV) {
  const all = [...STATIC_BANK.issues, ...SIGN_ISSUES];
  if (all.length > 0) {
    // Visible in the terminal/browser console while you are adding questions.
    console.warn(
      `[question bank] ${all.length} item(s) were skipped:\n` +
        all.map((i) => `  • ${i.origin}${i.id ? ` (${i.id})` : ''}: ${i.message}`).join('\n'),
    );
  }
}
